import React, { useEffect, useRef, useState } from 'react';
import { submitFeedback } from '../services/api';
import './FeedbackModal.css';

const CATEGORIES = [
  { value: 'issue', label: 'Report an issue' },
  { value: 'suggestion', label: 'Make a suggestion' },
  { value: 'other', label: 'Other' },
];

export const FEEDBACK_DRAFT_KEY = 'feedbackDraft';

export function loadFeedbackDraft() {
  try {
    const raw = sessionStorage.getItem(FEEDBACK_DRAFT_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

export function clearFeedbackDraft() {
  sessionStorage.removeItem(FEEDBACK_DRAFT_KEY);
}

export default function FeedbackModal({
  open,
  onClose,
  appPath,
  appLabel,
  userEmail,
  userName,
  onNeedsGmailAuth,
  initialDraft,
}) {
  const [category, setCategory] = useState('issue');
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);
  const [sent, setSent] = useState(false);
  const [needsGmail, setNeedsGmail] = useState(false);
  const textareaRef = useRef(null);
  const draftAppliedRef = useRef(false);

  useEffect(() => {
    if (!open) {
      draftAppliedRef.current = false;
      return;
    }
    if (initialDraft && !draftAppliedRef.current) {
      setCategory(initialDraft.category || 'issue');
      setMessage(initialDraft.message || '');
      draftAppliedRef.current = true;
    } else if (!initialDraft) {
      setCategory('issue');
      setMessage('');
    }
    setError(null);
    setSent(false);
    setSending(false);
    setNeedsGmail(false);
    const t = setTimeout(() => textareaRef.current?.focus(), 50);
    return () => clearTimeout(t);
  }, [open, initialDraft]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === 'Escape' && !sending) onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, sending, onClose]);

  if (!open) return null;

  const persistDraft = () => {
    sessionStorage.setItem(FEEDBACK_DRAFT_KEY, JSON.stringify({
      message: message.trim(),
      category,
      appPath,
      appLabel,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const trimmed = message.trim();
    if (!trimmed || sending) return;
    setSending(true);
    setError(null);
    setNeedsGmail(false);
    try {
      await submitFeedback({
        message: trimmed,
        category,
        appPath,
        appLabel,
      });
      clearFeedbackDraft();
      setSent(true);
    } catch (err) {
      setError(err.message || 'Failed to send');
      if (err.needsGmailScope || err.needsReauth) {
        setNeedsGmail(true);
        persistDraft();
      }
    } finally {
      setSending(false);
    }
  };

  const handleAllowGmail = () => {
    persistDraft();
    if (onNeedsGmailAuth) onNeedsGmailAuth();
  };

  return (
    <div className="feedback-overlay" onClick={() => !sending && onClose()} role="presentation">
      <div
        className="feedback-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="feedback-title"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="feedback-header">
          <h2 id="feedback-title">Feedback</h2>
          <button
            type="button"
            className="feedback-close"
            onClick={onClose}
            disabled={sending}
            aria-label="Close"
          >
            ×
          </button>
        </div>

        {sent ? (
          <div className="feedback-success">
            <p>Thanks — your message was sent.</p>
            <button type="button" className="feedback-primary" onClick={onClose}>
              Done
            </button>
          </div>
        ) : (
          <form onSubmit={handleSubmit}>
            <p className="feedback-meta">
              Sending from <strong>{userName || userEmail || 'your account'}</strong>
              {appLabel ? <> · App: <strong>{appLabel}</strong></> : null}
            </p>

            <label className="feedback-label" htmlFor="feedback-category">
              Type
            </label>
            <select
              id="feedback-category"
              className="feedback-select"
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              disabled={sending}
            >
              {CATEGORIES.map((c) => (
                <option key={c.value} value={c.value}>{c.label}</option>
              ))}
            </select>

            <label className="feedback-label" htmlFor="feedback-message">
              Message
            </label>
            <textarea
              id="feedback-message"
              ref={textareaRef}
              className="feedback-textarea"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Describe the issue or suggestion…"
              rows={6}
              maxLength={5000}
              disabled={sending}
              required
            />

            {error && (
              <div className="feedback-error">
                <p>{error}</p>
                {needsGmail && onNeedsGmailAuth && (
                  <button type="button" className="feedback-link-btn" onClick={handleAllowGmail}>
                    Allow Gmail
                  </button>
                )}
              </div>
            )}

            <div className="feedback-actions">
              <button
                type="button"
                className="feedback-secondary"
                onClick={onClose}
                disabled={sending}
              >
                Cancel
              </button>
              {needsGmail && onNeedsGmailAuth ? (
                <button
                  type="button"
                  className="feedback-primary"
                  onClick={handleAllowGmail}
                  disabled={sending}
                >
                  Allow Gmail
                </button>
              ) : (
                <button
                  type="submit"
                  className="feedback-primary"
                  disabled={sending || !message.trim()}
                >
                  {sending ? 'Sending…' : 'Send'}
                </button>
              )}
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
